import { Loader2Icon, TagIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsControllerSetDocumentCategory, type ForUserError } from 'korint-sdk';
import { useTranslation } from 'react-i18next';

import type { AIClassificationMetadata } from '@folio/components/features/document-viewer';
import type { DocumentCustomCategorieMapping } from '@folio/components/features/document-viewer/types';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';

import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover/popover';
import { SearchableSelect } from '../../ui/searchable-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip/tooltip';
import { useToast } from '../../ui/use-toast';
import { Stack } from '@folio/components/ui/flex';
import { Title } from '@folio/components/ui/text';

interface DocumentClassificationButtonProps {
  documentId: string;
  currentCategory?: string;
  categories: DocumentCustomCategorieMapping[];
  disabled?: boolean;
  aiClassificationMetadata?: AIClassificationMetadata;
}

export function DocumentClassificationButton({
  documentId,
  currentCategory,
  categories,
  disabled,
  aiClassificationMetadata,
}: DocumentClassificationButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const categoryOptions = useMemo(
    () =>
      categories.map(({ category }) => ({
        value: category,
        label: t(`documentReview.category.${category}`, {
          defaultValue: getDocumentCategoryLabel(category, categories, t),
        }),
      })),
    [categories, t],
  );

  const { mutate: updateDocument, isPending } = useMutation({
    mutationFn: (categoryValue: string) => {
      return documentsControllerSetDocumentCategory(documentId, {
        category: categoryValue,
        ...(aiClassificationMetadata && {
          aiCorrection: {
            originalCategory:
              aiClassificationMetadata.suggestedCategory ??
              aiClassificationMetadata.suggestedcategory ??
              '',
          },
        }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setOpen(false);
    },
    onError: (error: ForUserError) => {
      if (error.userError) return;
      toast({
        title: t('fileInput.classificationErrorTitle'),
        description: t('fileInput.classificationErrorDescription'),
      });
    },
  });

  const handleCategoryChange = (categoryValue: string) => {
    if (categoryValue) {
      updateDocument(categoryValue);
    }
  };

  return (
    <Tooltip delayDuration={0}>
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              aria-label={t('fileInput.classifyDocument')}
              variant="ghost"
              size="icon"
              className="h-9 w-9 md:h-8 md:w-8 hover:text-foreground hover:bg-slate-100"
              disabled={disabled || isPending}
            >
              {isPending ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <TagIcon className="w-4 h-4" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('fileInput.classifyDocument')}</p>
        </TooltipContent>
        <PopoverContent
          className="w-[calc(100vw-2rem)] sm:w-80 p-4"
          align="end"
          side="bottom"
          collisionPadding={16}
        >
          <Stack gap={3}>
            <div>
              <Title order={4} size="sm" className="mb-1 font-medium">
                {t('fileInput.classifyDocument')}
              </Title>
            </div>
            <SearchableSelect
              value={currentCategory}
              options={categoryOptions}
              onChange={handleCategoryChange}
              disabled={isPending}
              placeholder={t('fileInput.searchCategory')}
              searchable
            />
          </Stack>
        </PopoverContent>
      </Popover>
    </Tooltip>
  );
}
